import { useCallback, useEffect, useRef, useState } from 'react'

export interface CopyFeedback {
  /** The button reads "copied" right now. */
  copied: boolean
  /** Runs the copy; flips `copied` on only when it resolved. Resolves to whether it did. */
  copy: (run: () => Promise<void>) => Promise<boolean>
}

/**
 * The "copied" moment of a copy button: on after a copy that succeeded, off
 * again `resetMs` later. Each new attempt first turns it off; when attempts
 * overlap, only the latest one decides. A copy that failed never says copied;
 * it is reported on the console.
 */
export function useCopyFeedback(resetMs: number): CopyFeedback {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptRef = useRef(0)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  const copy = useCallback(
    async (run: () => Promise<void>) => {
      const attempt = ++attemptRef.current
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      setCopied(false)
      try {
        await run()
      } catch (cause) {
        console.warn('Copy failed', cause)
        return false
      }
      if (attempt !== attemptRef.current) return false
      setCopied(true)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setCopied(false)
      }, resetMs)
      return true
    },
    [resetMs]
  )

  return { copied, copy }
}
